import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { EuStars } from "../components/EuStars";
import { useLang } from "../lib/i18n";

export default function Signup() {
    const { signup } = useAuth();
    const { T } = useLang();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const invite = params.get("invite");
    const planParam = params.get("plan");
    const cycleParam = params.get("cycle") === "annual" ? "annual" : "monthly";
    const seatsParam = Math.max(1, parseInt(params.get("seats") || "3", 10) || 3);

    const [name, setName] = useState("");
    const [email, setEmail] = useState(params.get("email") || "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

    useEffect(() => {
        const e = params.get("email");
        if (e) setEmail(e);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const NAME_RE = /^[\p{L}][\p{L}'\u2019 -]{1,79}$/u;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!NAME_RE.test(name.trim())) {
            setError("Name may only contain letters, spaces, hyphens and apostrophes (2–80 characters).");
            return;
        }
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await signup(email, name, password, invite || undefined);
            if (invite) {
                navigate("/app/team", { replace: true });
                return;
            }
            if (planParam === "professional" || planParam === "team") {
                try {
                    const { data } = await api.post("/billing/checkout", {
                        plan_key: planParam,
                        cycle: cycleParam,
                        seats: planParam === "team" ? seatsParam : 1,
                    });
                    if (data.external && /^https?:/.test(data.hosted_url || "")) {
                        window.location.href = data.hosted_url;
                        return;
                    }
                    navigate(`/app/billing/checkout/${data.id}`, { replace: true });
                    return;
                } catch {
                    // fall through to verification if checkout could not start
                }
            }
            navigate(
                res?.verification_required === false ? "/app/onboarding" : "/verify-email",
                {
                    replace: true,
                    state: { devUrl: res?.dev_verify_url || "", email },
                }
            );
        } catch (err) {
            if (err?.response?.status === 429) {
                setError("Too many signups from this network. Please wait a few minutes and try again.");
            } else {
                const d = err?.response?.data?.detail;
                setError(typeof d === "string" ? d : "Could not create account. Try a different email.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#F5F0E8]">
            <section className="hidden lg:flex flex-col justify-between p-14 bg-[#0D1B3E] text-[#F5F0E8]">
                <Link to="/" className="flex items-center gap-3" data-testid="signup-brand">
                    <EuStars size={36} />
                    <span className="font-display text-xl">EuroLaw Hub</span>
                </Link>
                <div>
                    <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                        {invite ? T("You have been invited", "Davet edildiniz") : T("7-day free trial", "7 günlük ücretsiz deneme")}
                    </div>
                    <h1 className="font-display text-4xl mt-3 leading-[1.1] tracking-tight">
                        {invite ? (
                            <>Join your team's<br />shared workspace.</>
                        ) : (
                            <>{T("Full access, 7 days.", "Tam erişim, 7 gün.")}<br />{T("No credit card.", "Kredi kartı gerekmez.")}</>
                        )}
                    </h1>
                    <p className="mt-5 text-[#F5F0E8]/70 max-w-md">
                        {invite
                            ? "Create your account to accept the invitation. You'll share pooled credits and the full Team feature set."
                            : T("Try every module on real EU law questions: 15 credits, document uploads included. Upgrade only when you're convinced.",
                                "Tüm modülleri gerçek hukuk sorularında deneyin: 15 kredi ve belge yükleme dahildir. Yalnızca ikna olduğunuzda yükseltin.")}
                    </p>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#F5F0E8]/50">
                    GDPR · AI Act · DORA · NIS2 · CJEU
                </div>
            </section>

            <section className="flex items-center justify-center p-8 lg:p-14">
                <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="signup-form">
                    <div className="lg:hidden flex items-center gap-3 mb-10">
                        <EuStars size={28} />
                        <span className="font-display text-lg">EuroLaw Hub</span>
                    </div>
                    <div className="label-eyebrow">{T("Create account", "Hesap oluştur")}</div>
                    <h2 className="font-display text-3xl lg:text-4xl mt-2 tracking-tight">
                        {invite ? T("Accept your invitation.", "Davetinizi kabul edin.") : T("Start your 7-day free trial.", "7 günlük ücretsiz denemenizi başlatın.")}
                    </h2>
                    <p className="mt-3 text-sm text-[#0D1B3E]/70">
                        {T("Already a member?", "Zaten üye misiniz?")}{" "}
                        <Link
                            to={invite ? `/login?next=${encodeURIComponent(`/invite/${invite}`)}` : "/login"}
                            className="underline underline-offset-4 text-[#0D1B3E] hover:text-[#C9A84C]"
                            data-testid="signup-link-login"
                        >
                            {T("Sign in", "Giriş yap")}
                        </Link>
                    </p>
                    {planParam && !invite && (
                        <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#C9A84C]" data-testid="signup-plan-note">
                            Selected plan: {planParam}
                            {planParam === "team" ? ` · ${seatsParam} seats` : ""} · {cycleParam} · checkout follows after signup
                        </p>
                    )}

                    <div className="mt-8 space-y-4">
                        <Field label={T("Full name", "Ad soyad")} value={name} onChange={setName} testId="signup-input-name" required />
                        <Field
                            label={T("Work email", "İş e-postası")}
                            type="email"
                            value={email}
                            onChange={setEmail}
                            testId="signup-input-email"
                            autoComplete="email"
                            required
                        />
                        <PasswordField
                            label={T("Password", "Şifre")}
                            value={password}
                            onChange={setPassword}
                            testId="signup-input-password"
                            autoComplete="new-password"
                        />
                        <div>
                            <PasswordField
                                label={T("Confirm password", "Şifreyi doğrulayın")}
                                value={confirmPassword}
                                onChange={setConfirmPassword}
                                testId="signup-input-confirm-password"
                                autoComplete="new-password"
                                invalid={mismatch}
                            />
                            {mismatch && (
                                <p
                                    className="mt-2 text-xs text-red-700"
                                    data-testid="signup-password-mismatch"
                                >
                                    {T("Passwords do not match.", "Şifreler eşleşmiyor.")}
                                </p>
                            )}
                        </div>
                        <p className="text-xs text-[#0D1B3E]/55">
                            {T("At least 8 characters. Use a strong, unique password.", "En az 8 karakter. Güçlü ve benzersiz bir şifre kullanın.")}
                        </p>
                    </div>

                    {error && (
                        <div
                            data-testid="signup-error"
                            className="mt-4 text-sm border border-red-500/40 bg-red-50 text-red-900 px-3 py-2"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting || mismatch}
                        className="btn-primary mt-7 w-full disabled:opacity-60"
                        data-testid="signup-submit-button"
                    >
                        {submitting ? T("Creating account…", "Hesap oluşturuluyor…") : invite ? T("Create account & join team", "Hesap oluştur ve ekibe katıl") : T("Create account", "Hesap oluştur")}
                    </button>
                </form>
            </section>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testId, ...rest }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="field"
                data-testid={testId}
                {...rest}
            />
        </label>
    );
}

function PasswordField({ label, value, onChange, testId, invalid = false, ...rest }) {
    const [show, setShow] = useState(false);
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/70 mb-2">
                {label}
            </span>
            <div className="relative">
                <input
                    type={show ? "text" : "password"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={`field pr-11 ${invalid ? "border-red-500" : ""}`}
                    data-testid={testId}
                    required
                    {...rest}
                />
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50 hover:text-[#0D1B3E]"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid={`${testId}-toggle`}
                >
                    {show ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
            </div>
        </label>
    );
}
